import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/transfert/chauffeur-transfert.png";
import { cityPriceFaq } from "../slugs/cityPricing";

const sainteMaximeEn: ChauffeurPage = {
  slug: "sainte-maxime",
  title: "VTC & private chauffeur in Sainte-Maxime",
  subtitle:
    "Private chauffeur in Sainte-Maxime: Nice airport transfers (~1 h 15), Saint-Raphaël TGV station shuttle, Saint-Tropez link and chauffeur hire, on the gulf, 7 days a week.",
  keywords: [
    "vtc sainte-maxime",
    "private chauffeur sainte-maxime",
    "sainte-maxime gulf of saint-tropez chauffeur",
    "sainte-maxime nice airport transfer",
  ],
  hero: {
    title: "VTC & private chauffeur in Sainte-Maxime",
    subtitle:
      "Facing Saint-Tropez across the gulf: Nice airport transfers (~1 h 15), TGV station shuttle and Saint-Tropez link, 24/7.",
    keywords: [
      "VTC Sainte-Maxime",
      "gulf of Saint-Tropez chauffeur",
      "Saint-Tropez link",
      "Nice airport transfer",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Private chauffeur & VTC in Sainte-Maxime, facing Saint-Tropez",
    badges: [
      "Nice airport ~1 h 15",
      "Saint-Tropez link",
      "Bilingual chauffeurs",
    ],
  },
  benefits: {
    heading: "Why a chauffeur in Sainte-Maxime",
    items: [
      {
        title: "Facing Saint-Tropez",
        description:
          "Across the gulf, minutes away by boat: the chauffeur links you to the shuttle-boat pier to skip the peninsula's traffic.",
      },
      {
        title: "Nice airport in ~1 h 15",
        description:
          "The gulf of Saint-Tropez sits away from the motorway: we allow a comfortable margin and track your flight for this longer journey.",
      },
      {
        title: "Saint-Raphaël TGV station",
        description:
          "The nearest station, at the mouth of the gulf: a dedicated shuttle for your TGV departures and arrivals.",
      },
      {
        title: "Beaches and promenade",
        description:
          "La Nartelle, the Simon-Lorière promenade, the port: drop-off as close as possible, pick-up on call, rated 5.0/5 from 38 Google reviews.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services in Sainte-Maxime",
    services: [
      {
        title: "Nice airport transfers",
        text: "Sainte-Maxime ⇄ Nice Côte d'Azur airport in ~1 h 15, with flight tracking and a comfortable margin.",
      },
      {
        title: "Saint-Raphaël TGV station shuttle",
        text: "A link to Saint-Raphaël-Valescure station, the nearest for reaching the TGV from the gulf.",
      },
      {
        title: "Saint-Tropez link",
        text: "Drop-off at the shuttle-boat pier or a road journey around the gulf, depending on the crowds.",
      },
      {
        title: "Chauffeur hire",
        text: "By the hour or by the day, between beaches, port and the gulf's hinterland.",
      },
      {
        title: "Long distance from Cannes/Nice",
        text: "Journeys from Cannes, Nice or the airport to Sainte-Maxime, by saloon, van or VIP minibus.",
      },
    ],
  },
  locations: {
    heading: "Places & pick-up points",
    items: [
      {
        name: "Port & Simon-Lorière promenade",
        description: "Seafront, shuttle pier and town centre.",
      },
      {
        name: "La Nartelle beach",
        description: "The bay's large beach, private clubs and restaurants.",
      },
      {
        name: "Tour Carrée & old town",
        description: "Museum and lanes of the historic centre.",
      },
      {
        name: "Casino & Sémaphore",
        description: "Lively seafront and viewpoint over the gulf.",
      },
      {
        name: "Saint-Raphaël-Valescure station",
        description: "Nearest TGV station, at the mouth of the gulf.",
      },
      {
        name: "Nice airport",
        description: "Terminals 1 and 2, ~1 h 15 via the A8.",
      },
    ],
  },
  generic: {
    heading: "A chauffeur facing Saint-Tropez",
    subheading:
      "Sainte-Maxime looks across the gulf to Saint-Tropez: beaches to the north, the peninsula opposite.",
    paragraphs: [
      "Set on the northern shore of the gulf of Saint-Tropez, Sainte-Maxime faces Saint-Tropez and combines beaches, a long promenade and a lively port. KS Rent Services handles your journeys here with a private chauffeur: transfers, station shuttle, a link to the peninsula and chauffeur hire by the hour or by the day.",
      "Nice Côte d'Azur airport is around 1 h 15 away: as the gulf lies away from the A8, this transfer is noticeably longer than on the eastern Riviera. We allow a comfortable margin and track your flight; Saint-Raphaël TGV station, at the mouth of the gulf, completes the access.",
      "To reach Saint-Tropez, the chauffeur drops you at the shuttle-boat pier — a few minutes' crossing that avoids the gulf-road jams — or takes the road around the gulf depending on the crowds and your luggage.",
    ],
    bullets: [
      "Nice airport in ~1 h 15",
      "Shuttle-boat link to Saint-Tropez",
      "Saint-Raphaël TGV station",
    ],
  },
  process: {
    heading: "Book a chauffeur in Sainte-Maxime",
    steps: [
      "Tell us your route (airport, Saint-Raphaël station, Saint-Tropez, beaches) and your times.",
      "We confirm the vehicle, the margin for Nice airport and the fixed package.",
      "The chauffeur tracks your flight or train and greets you on arrival.",
      "Drop-off at the pier or chauffeur hire on the gulf, pick-up on request.",
    ],
  },
  faq: [
    cityPriceFaq("en", "Sainte-Maxime"),
    {
      q: "How long to Nice airport?",
      a: "Around 1 h 15. As the gulf of Saint-Tropez lies away from the A8, this transfer is longer: we allow a comfortable margin and track your flight.",
    },
    {
      q: "How do I reach Saint-Tropez from Sainte-Maxime?",
      a: "The simplest way is the shuttle boat, a few minutes' crossing that avoids the jams; we drop you at the pier, or drive around the gulf depending on the crowds.",
    },
    {
      q: "Which is the nearest TGV station?",
      a: "Saint-Raphaël-Valescure, at the mouth of the gulf: we provide the dedicated shuttle for your TGV departures and arrivals.",
    },
    {
      q: "How far in advance should I book?",
      a: "Book as early as possible in summer, a very busy period on the gulf; we also handle last-minute requests subject to availability.",
    },
    {
      q: "Do your chauffeurs speak English?",
      a: "Yes, our chauffeurs are bilingual French/English, used to an international clientele.",
    },
    {
      q: "Which vehicles do you offer?",
      a: "Mercedes E-Class and S-Class saloons, 7-seat vans, SUVs and VIP minibuses, depending on passengers and luggage.",
    },
  ],
  relatedLinks: [
    {
      label: "Private car service in Saint-Tropez",
      href: "/en/chauffeur/saint-tropez",
    },
    { label: "Private car service in Cannes", href: "/en/chauffeur/cannes" },
    {
      label: "Private driver on the French Riviera",
      href: "/en/chauffeur/french-riviera",
    },
    {
      label: "Rent a car in Sainte-Maxime",
      href: "/en/location/car-rental-sainte-maxime",
    },
  ],
  cta: {
    title: "Your private chauffeur in Sainte-Maxime",
    text: "Nice airport transfer, Saint-Tropez link or chauffeur hire on the gulf: fast reply, 7 days a week.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "Car service Sainte-Maxime — private chauffeur & transfers",
    description:
      "Private car service in Sainte-Maxime: Nice airport transfers (~1 h 15), Saint-Raphaël TGV station shuttle, Saint-Tropez link and gulf beaches.",
    canonical: "/en/chauffeur/sainte-maxime",
  },
};

export default sainteMaximeEn;
